import { useEffect, useRef, useState, type PropsWithChildren } from "react";
import { createPortal } from "react-dom";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@porchpass/ui";
import { Cancel01Icon, InformationCircleIcon } from "@porchpass/ui";

/**
 * Keeps bottom sheets as wide as the form on desktop instead of the whole
 * window. The trailing `!` is needed because @porchpass/ui's own `m-2` loads
 * after the app's utilities and would otherwise reset the auto margins.
 */
export const SHEET_WIDTH_CLASSES = "md:max-w-[768px] md:mx-auto!";

type SlideUpModalProps = {
  isOpen: boolean;
  /** Changes when the modal is opened for a different record/session. */
  openSession?: unknown;
  title: string | React.ReactNode;
  subText?: string;
  hasIcon?: boolean;
  closeHandler: () => unknown;
  hasDismissButton?: boolean;
  dismissHandler?: () => unknown;
  dismissButtonText?: string;
  actionButtonText?: string | React.ReactNode;
  actionButtonClickHandler?: () => void;
  variant?: "primary" | "secondary" | "danger";
  isLoading?: boolean;
  /**
   * When true, the sheet can be closed by clicking outside or dragging down.
   * Defaults to false for edit flows with Cancel + Save/Submit actions, and
   * true for informational sheets without unsaved form data.
   */
  dismissible?: boolean;
  /** Shows the header close icon. Prefer footer actions for edit flows. */
  showCloseButton?: boolean;
} & PropsWithChildren;

export const SlideUpModal = ({
  isOpen,
  openSession,
  title,
  subText,
  children,
  closeHandler,
  dismissHandler,
  hasIcon,
  hasDismissButton = true,
  dismissButtonText = "Cancel",
  actionButtonText,
  actionButtonClickHandler,
  variant = "primary",
  isLoading,
  dismissible,
  showCloseButton = false,
}: SlideUpModalProps) => {
  const hasActionButton = !!actionButtonText && !!actionButtonClickHandler;
  const isEditFlow = hasDismissButton && hasActionButton;
  const canDismissOnOutsideClick = dismissible ?? !isEditFlow;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pendingCloseActionRef = useRef<(() => unknown) | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      pendingCloseActionRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setDrawerOpen(false);
      return;
    }

    pendingCloseActionRef.current = null;
    setDrawerOpen(false);
    const frame = requestAnimationFrame(() => {
      if (isMountedRef.current) {
        setDrawerOpen(true);
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [isOpen, openSession]);

  const initiateClose = (action: () => unknown = closeHandler) => {
    setDrawerOpen(false);
    // Always sync parent state immediately. Deferred close left edit modals mounted
    // with stale form data when onAnimationEnd did not fire (e.g. headless CI).
    action();
    pendingCloseActionRef.current = null;
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDrawerOpen(true);
      return;
    }

    if (!canDismissOnOutsideClick) {
      return;
    }

    setDrawerOpen(false);
    closeHandler();
  };

  const handleAnimationEnd = (open: boolean) => {
    if (open || !isMountedRef.current) {
      return;
    }

    pendingCloseActionRef.current = null;
  };

  const actionVariant =
    variant === "danger"
      ? "destructive"
      : variant === "secondary"
        ? "secondary"
        : "primary";

  const modal = (
    <Drawer
      open={drawerOpen}
      dismissible={canDismissOnOutsideClick}
      onOpenChange={handleOpenChange}
      onAnimationEnd={handleAnimationEnd}
      direction="bottom"
    >
      <DrawerContent
        aria-labelledby="modal-title"
        className={`flex max-h-[90vh] flex-col overflow-hidden ${SHEET_WIDTH_CLASSES}`}
      >
        <DrawerHeader className="flex shrink-0 flex-row items-start justify-between gap-4 text-left">
          <div>
            <DrawerTitle
              id="modal-title"
              className="flex items-center gap-2 text-lg font-semibold"
            >
              {hasIcon && (
                <InformationCircleIcon size={18} className="shrink-0" />
              )}
              {title ? title : null}
            </DrawerTitle>
            {subText && (
              <p className="mt-2 text-sm text-on-surface-muted">{subText}</p>
            )}
          </div>
          {showCloseButton && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => initiateClose(closeHandler)}
              aria-label="Close modal"
              className="shrink-0"
            >
              <Cancel01Icon size={19} />
            </Button>
          )}
        </DrawerHeader>
        <div
          id="slideup-modal-contents"
          className="min-h-0 overflow-y-auto px-4 pb-4 max-h-[calc(90vh-11rem)]"
          tabIndex={-1}
        >
          {children}
        </div>
        {(hasDismissButton || hasActionButton) && (
          <DrawerFooter className="flex shrink-0 flex-row justify-end gap-2">
            {hasDismissButton && (
              <Button
                variant="ghost"
                onClick={() => initiateClose(dismissHandler ?? closeHandler)}
              >
                {dismissButtonText}
              </Button>
            )}
            {hasActionButton && (
              <Button
                variant={actionVariant}
                onClick={actionButtonClickHandler}
                disabled={isLoading}
              >
                {actionButtonText}
              </Button>
            )}
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );

  return createPortal(modal, document.body);
};
